import { useState, useEffect, useCallback } from 'react'
import { Plus, Trash2, X, Check, ShoppingCart } from 'lucide-react'
import { api } from '../services/api'
import { formatCurrency, getCurrentMonth } from '../utils/format'

// Mapping list_category value → nom exact de la catégorie dans la DB
const LIST_TO_CAT_NAME = {
  petit_dejeuner: 'Petit-déjeuner',
  repas: 'Repas',
  vetements_enfants: 'Vêtements Enfants',
  jouets: 'Jouets',
  hygiene_enfants: 'Hygiène Enfants',
  nutrition_enfants: 'Nutrition Enfants',
  electromenager: 'Électroménager',
  cuisine: 'Cuisine',
  menage: 'Ménage',
}

const groceryCategories = [
  { value: 'alimentation', label: '🥘 Alimentation' },
  { value: 'hygiene', label: '🧴 Hygiène' },
  { value: 'menager', label: '🧹 Ménager' },
  { value: 'autre', label: '📦 Autre' },
]

const listCategories = [
  { value: '', label: 'Toutes les listes' },
  { value: 'petit_dejeuner', label: '☕ Petit-déjeuner' },
  { value: 'repas', label: '🍽️ Repas' },
  { value: 'vetements_enfants', label: '👕 Vêtements Enfants' },
  { value: 'jouets', label: '🧸 Jouets' },
  { value: 'hygiene_enfants', label: '🧴 Hygiène Enfants' },
  { value: 'nutrition_enfants', label: '🍼 Nutrition Enfants' },
  { value: 'vetements_parents', label: '👔 Vêtements Parents' },
  { value: 'hygiene_parents', label: '🧼 Hygiène Parents' },
  { value: 'electromenager', label: '🔌 Électroménager' },
  { value: 'cuisine', label: '🍳 Cuisine' },
  { value: 'menage', label: '🧹 Ménage' },
  { value: 'autre', label: '📦 Autre' },
]

export default function Groceries() {
  const [groceries, setGroceries] = useState([])
  const [allBudgets, setAllBudgets] = useState([]) // budgets du mois courant
  const [allCategories, setAllCategories] = useState([]) // catégories flattenées
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState('pending')
  const [listFilter, setListFilter] = useState('')
  const [formData, setFormData] = useState({ name: '', quantity: '1', unit_price: '', category: 'alimentation', list_category: '' })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [data, cats, buds] = await Promise.all([
        api.getGroceries(filter === 'all' ? '' : filter),
        api.getCategories(),
        api.getBudgets(getCurrentMonth()),
      ])
      setGroceries(data.groceries)
      const flat = (cats.categories || []).flatMap(p => [p, ...(p.children || [])])
      setAllCategories(flat)
      setAllBudgets(buds.budgets || [])
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }, [filter])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.some(ev => ev.entity === 'grocery')) {
        fetchData()
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [fetchData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name) return
    try {
      const price = parseFloat(formData.unit_price) || 0
      await api.addGrocery({
        ...formData,
        unit_price: price,
        estimated_price: price,
      })
      setShowForm(false)
      setFormData({ name: '', quantity: '1', unit_price: '', category: 'alimentation', list_category: '' })
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleTogglePurchased = async (item) => {
    try {
      await api.updateGrocery({ ...item, is_purchased: item.is_purchased ? 0 : 1 })
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet article ?')) return
    try {
      await api.deleteGrocery(id)
      fetchData()
    } catch (err) {
      alert(err.message)
    }
  }

  const totalEstimated = groceries.filter(g => !g.is_purchased && (!listFilter || g.list_category === listFilter)).reduce((sum, g) => sum + (g.estimated_price || 0), 0)
  const filteredGroceries = listFilter ? groceries.filter(g => g.list_category === listFilter) : groceries

  // Helper : budget d'une catégorie par son nom
  function getBudgetByName(name) {
    const cat = allCategories.find(c => c.name === name)
    if (!cat) return null
    return allBudgets.find(b => b.category_id === cat.id) || null
  }

  // Helper : total estimé des courses d'une list_category
  function getCategoryTotal(listCatValue, onlyPending = false) {
    return groceries
      .filter(g => g.list_category === listCatValue && (!onlyPending || !g.is_purchased))
      .reduce((s, g) => s + (g.estimated_price || 0), 0)
  }

  // Totaux Alimentation (petit_dejeuner + repas) — lien CDC
  const alimentationLists = ['petit_dejeuner', 'repas']
  const alimentationSpent = alimentationLists.reduce((s, l) => s + getCategoryTotal(l), 0)
  const alimentationBudget = alimentationLists.reduce((s, name) => {
    const bName = LIST_TO_CAT_NAME[name]
    const b = getBudgetByName(bName)
    return s + (b?.amount || 0)
  }, 0)

  // Info de budget pour la liste sélectionnée
  const selectedBudgetName = listFilter ? LIST_TO_CAT_NAME[listFilter] : null
  const selectedBudget = selectedBudgetName ? getBudgetByName(selectedBudgetName) : null
  const selectedTotal = listFilter ? getCategoryTotal(listFilter) : 0
  const selectedPending = listFilter ? getCategoryTotal(listFilter, true) : 0

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" /></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Liste de courses</h2>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-1.5 px-3 py-2">
          <Plus size={18} /> Ajouter
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {['pending', 'purchased', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f ? 'bg-primary-700 text-white' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {f === 'pending' ? 'À acheter' : f === 'purchased' ? 'Achétés' : 'Tous'}
          </button>
        ))}
      </div>

      {/* List category filter */}
      <select
        value={listFilter}
        onChange={(e) => setListFilter(e.target.value)}
        className="input py-1.5 text-sm"
      >
        {listCategories.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      {/* Résumé Alimentation (CDC : repas + petit-déjeuner = dépense alimentaire réelle) */}
      {!listFilter && alimentationBudget > 0 && (
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-green-700">🍽️ Alimentation (Budget)</span>
            <span className="text-sm font-bold text-green-700">{formatCurrency(alimentationBudget)}</span>
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden mb-1">
            <div
              className={`h-full rounded-full ${alimentationSpent > alimentationBudget ? 'bg-red-400' : 'bg-green-500'}`}
              style={{ width: `${Math.min((alimentationSpent / alimentationBudget) * 100, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-green-600">
            <span>Courses : {formatCurrency(alimentationSpent)}</span>
            <span className={alimentationSpent > alimentationBudget ? 'text-red-500 font-medium' : ''}>
              {alimentationSpent > alimentationBudget ? 'Dépassement' : `Reste : ${formatCurrency(alimentationBudget - alimentationSpent)}`}
            </span>
          </div>
        </div>
      )}

      {/* Budget de la liste sélectionnée */}
      {listFilter && selectedBudget && (
        <div className="card bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-primary-700">Budget {selectedBudgetName}</span>
            <span className="text-sm font-bold text-primary-700">{formatCurrency(selectedBudget.amount)}</span>
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden mb-1">
            <div
              className={`h-full rounded-full ${selectedTotal > selectedBudget.amount ? 'bg-red-400' : 'bg-primary-600'}`}
              style={{ width: `${Math.min((selectedTotal / selectedBudget.amount) * 100, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-primary-600">
            <span>Courses : {formatCurrency(selectedTotal)}{selectedPending !== selectedTotal ? ` (${formatCurrency(selectedPending)} restant)` : ''}</span>
            <span className={selectedTotal > selectedBudget.amount ? 'text-red-500 font-medium' : ''}>
              {selectedTotal > selectedBudget.amount ? `Dépassement ${formatCurrency(selectedTotal - selectedBudget.amount)}` : `Reste : ${formatCurrency(selectedBudget.amount - selectedTotal)}`}
            </span>
          </div>
        </div>
      )}

      {/* Total estimé si pas de budget lié */}
      {filter === 'pending' && totalEstimated > 0 && !selectedBudget && (
        <div className="card bg-primary-50 border-primary-200 text-center">
          <p className="text-sm text-slate-500">Total estimé</p>
          <p className="text-xl font-bold text-primary-700">{formatCurrency(totalEstimated)}</p>
        </div>
      )}

      <div className="space-y-2">
        {filteredGroceries.length === 0 && (
          <div className="card text-center py-8 text-slate-400">
            <ShoppingCart size={32} className="mx-auto mb-2" />
            <p>Aucun article</p>
          </div>
        )}
        {filteredGroceries.map((item) => {
          const cat = groceryCategories.find(c => c.value === item.category)
          const listCat = listCategories.find(c => c.value === item.list_category)
          return (
            <div key={item.id} className={`card flex items-center gap-3 ${item.is_purchased ? 'opacity-60' : ''}`}>
              <button
                onClick={() => handleTogglePurchased(item)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  item.is_purchased ? 'bg-primary-700 border-primary-700' : 'border-slate-300'
                }`}
              >
                {item.is_purchased && <Check size={14} className="text-white" />}
              </button>
              <div className="flex-1">
                <p className={`font-medium text-sm ${item.is_purchased ? 'line-through' : ''}`}>{item.name}</p>
                <p className="text-xs text-slate-400">
                  {cat?.label || item.category} • Qté: {item.quantity}
                  {item.estimated_price > 0 && ` • ${formatCurrency(item.estimated_price)}`}
                </p>
                {listCat && listCat.value && (
                  <span className="badge bg-primary-50 text-primary-600 text-[10px] mt-1">{listCat.label}</span>
                )}
              </div>
              <button onClick={() => handleDelete(item.id)} className="p-1.5 text-slate-300 active:text-red-500">
                <Trash2 size={16} />
              </button>
            </div>
          )
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Nouvel article</h3>
              <button onClick={() => setShowForm(false)} className="p-1"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="label">Nom de l'article</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input" placeholder="Ex: Riz, savon..." required autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Quantité</label>
                  <input type="text" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} className="input" placeholder="1" />
                </div>
                <div>
                  <label className="label">Prix unitaire</label>
                  <input type="number" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })} className="input" placeholder="0" />
                </div>
              </div>
              <div>
                <label className="label">Catégorie</label>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="input">
                  {groceryCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Liste d'achat</label>
                <select value={formData.list_category} onChange={(e) => setFormData({ ...formData, list_category: e.target.value })} className="input">
                  {listCategories.filter(c => c.value).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <button type="submit" className="btn-primary w-full">Ajouter</button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
